import { useEffect, useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { OptionKey, Pagination, PaginationParams } from '@/common/types';
import { API_URL, ILockboxDto } from '@/dtos';
import {
  LockboxListItem,
  LockboxPaymentMethod,
  LockboxPaymentType,
  LockboxStatus,
  LockboxType,
  WireStatus,
} from '@/modules/employer/components/Lockbox/lockbox.types';
import { joinArrayWithCommas } from '@/utils';


interface LockboxesQueryParams extends Partial<PaginationParams> {
  types?: OptionKey[];
  statuses?: OptionKey[];
  paymentTypes?: OptionKey[];
}

interface LockboxesQueryProps extends LockboxesQueryParams {
  onSuccess?: (data?: LockboxListItem[]) => void;
  enabled: boolean;
}

const DEFAULT_TYPES = [
  LockboxType.INBOUND_TOA,
  LockboxType.OUTBOUND_TOA,
  LockboxType.RETURNED_CHECK,
  LockboxType.UNKNOWN,
  LockboxType.CLAIM_REPAYMENT,
  LockboxType.BENEFICIARY,
  LockboxType.HSA_FORM,
  LockboxType.EXCESS_CONTRIBUTION,
  LockboxType.COMBINE_HSA,
  LockboxType.MISTAKEN_DISTRIBUTION,
  LockboxType.HSA_ROLLOVER_FORM,
  LockboxType.HSA_CONTRIBUTION_FORM,
  LockboxType.HSA_TRANSFER_DIVORCE,
  LockboxType.HSA_ACCOUNT_CLOSURE,
  LockboxType.DEATH_DISTRIBUTION,
  LockboxType.POWER_OF_ATTORNEY,
  LockboxType.RETURNED_MAIL,
];

const DEFAULT_STATUSES = [
  LockboxStatus.NEW,
  LockboxStatus.PENDING,
  LockboxStatus.DECLINED,
  LockboxStatus.APPROVED,
  LockboxStatus.PROCESSING,
  LockboxStatus.INVESTMENTS_LIQUIDATED,
  LockboxStatus.COMPLETED,
  LockboxStatus.CANCELED,
  LockboxStatus.DIVIDED,
  LockboxStatus.PARTIALLY_APPROVED,
  LockboxStatus.PROCESSED_MANUALLY,
];

const useLockboxesQuery = ({
  page,
  perPage,
  searchString,
  types,
  statuses,
  paymentTypes,
  enabled,
  onSuccess,
}: LockboxesQueryProps) => {
  const wireStatusParams = useMemo(() => {
    if (!paymentTypes?.length) {
      return {};
    }

    const paymentTypesMap = new Map(paymentTypes?.map((key) => [key, true]));

    const wireStatuses = [
      paymentTypesMap.has(LockboxPaymentType.WIRE_SENT) ? WireStatus.SENT : undefined,
      paymentTypesMap.has(LockboxPaymentType.WIRE_NOT_SENT) ? WireStatus.NOT_SENT : undefined,
    ].filter(Boolean);

    const paymentMethods = [
      paymentTypesMap.has(LockboxPaymentType.CHECK) ? LockboxPaymentMethod.CHECK : undefined,
      paymentTypesMap.has(LockboxPaymentType.WIRE_SENT) ||
      paymentTypesMap.has(LockboxPaymentType.WIRE_NOT_SENT)
        ? LockboxPaymentMethod.WIRE
        : undefined,
    ].filter(Boolean);

    return {
      ...(wireStatuses?.length ? { wire_statuses: joinArrayWithCommas(wireStatuses) } : {}),
      ...(paymentMethods?.length ? { payment_methods: joinArrayWithCommas(paymentMethods) } : {}),
    };
  }, [paymentTypes]);

  const params = {
    page: page && page >= 0 ? page : 0,
    size: perPage || 50,
    types: types?.length ? joinArrayWithCommas(types) : joinArrayWithCommas(DEFAULT_TYPES),
    statuses: statuses?.length ? joinArrayWithCommas(statuses) : joinArrayWithCommas(DEFAULT_STATUSES),
    ...(searchString ? { search_value: searchString } : {}),
    ...wireStatusParams,
  };

  const { data, isLoading, isError } = useQuery(
    [API_URL.LOCKBOXES, page, perPage, searchString, types, statuses, paymentTypes],
    () => api.get<Pagination<ILockboxDto>>(API_URL.LOCKBOXES, { params }),
    {
      enabled: enabled,
    }
  );

  const formattedData = useMemo((): LockboxListItem[] => {
    const lockboxes = data?.data?.content || [];

    return lockboxes?.map(
      (lockbox): LockboxListItem => ({
        id: toString(lockbox?.id),
        name: lockbox?.name,
        accountNumber: lockbox?.account_number != null ? String(lockbox.account_number) : undefined,
        amount: lockbox?.amount,
        employeeName: lockbox?.employee_name,
        requestDate: lockbox?.request_date,
        lockboxStatus: lockbox?.lockbox_status as unknown as LockboxStatus,
        lockboxType: lockbox?.lockbox_type as unknown as LockboxType,
      })
    );
  }, [data]);

  useEffect(() => {
    if (onSuccess && enabled && formattedData?.length) {
      onSuccess(formattedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedData]);

  return {
    total: data?.data?.totalElements || 0,
    formattedData,
    isLoading,
    isError,
  };
};

export default useLockboxesQuery;
